import { html, nothing } from "lit";
import type { ToolCard } from "../types/chat-types.ts";
import { icons } from "../icons.ts";
import { formatToolDetail, resolveToolDisplay } from "../tool-display.ts";
import { TOOL_INLINE_THRESHOLD } from "./constants.ts";
import { tryParseJsonLoose } from "./structured-output.ts";
import { formatToolOutputForSidebar, getTruncatedPreview } from "./tool-helpers.ts";

const COMPACT_TOOL_NAMES = new Set([
  "read",
  "write",
  "edit",
  "str_replace",
  "strreplace",
  "bash",
  "exec",
  "grep",
  "glob",
  "glob_file_search",
  "list_dir",
  "listdir",
  "apply_patch",
  "run_terminal_cmd",
  "shell",
]);

function normalizeToolName(name: string): string {
  return name.trim().toLowerCase().replace(/-/g, "_");
}

export function isCompactToolRow(name: string): boolean {
  return COMPACT_TOOL_NAMES.has(normalizeToolName(name));
}

export function shouldUseToolRow(card: ToolCard): boolean {
  if (!isCompactToolRow(card.name)) {
    return false;
  }
  if (card.kind === "result" && card.text && tryParseJsonLoose(card.text) !== null) {
    return false;
  }
  return true;
}

export function renderToolRow(
  card: ToolCard,
  onOpenSidebar?: (content: string) => void,
  opts?: { preferRow?: boolean },
) {
  if (opts?.preferRow === false || !shouldUseToolRow(card)) {
    return null;
  }

  const display = resolveToolDisplay({ name: card.name, args: card.args });
  const detail = formatToolDetail(display);
  const hasText = Boolean(card.text?.trim());
  const canClick = Boolean(onOpenSidebar);
  const isShort = hasText && (card.text?.length ?? 0) <= TOOL_INLINE_THRESHOLD;

  const openSidebar = () => {
    if (!onOpenSidebar) {
      return;
    }
    if (hasText) {
      onOpenSidebar(formatToolOutputForSidebar(card.text!));
      return;
    }
    onOpenSidebar(
      `## ${display.label}\n\n${detail ? `**Command:** \`${detail}\`\n\n` : ""}*No output — tool completed successfully.*`,
    );
  };

  return html`
    <details class="chat-tool-row" @click=${(e: Event) => e.stopPropagation()}>
      <summary class="chat-tool-row__summary">
        <span class="chat-tool-row__icon">${icons[display.icon]}</span>
        <span class="chat-tool-row__label">${display.label}</span>
        ${detail ? html`<span class="chat-tool-row__detail muted">${detail}</span>` : nothing}
        ${
          canClick
            ? html`
                <button
                  type="button"
                  class="chat-tool-row__view btn subtle btn--sm"
                  @click=${(e: Event) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openSidebar();
                  }}
                >
                  View
                </button>
              `
            : nothing
        }
        <span class="chat-tool-row__chevron muted" aria-hidden="true">▸</span>
      </summary>
      ${
        hasText
          ? html`
              <div class="chat-tool-row__body mono">
                ${isShort ? card.text : getTruncatedPreview(card.text!)}
              </div>
            `
          : html`
              <div class="chat-tool-row__body muted">Completed</div>
            `
      }
    </details>
  `;
}
